import { PAGE_MEASURE } from '@/components/layout/PageFrame';
import { Skeleton } from '@/components/ui/skeleton';
import { PANEL_CONTAINER, SECTION_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { useRightDockStore } from '@/stores/useRightDockStore';

export function ContentBoardSkeleton(): JSX.Element {
  const hasGlobalRightDock = useRightDockStore((s) => s.activePanel !== null);

  return (
    <div
      aria-label="Loading content board"
      className="relative flex h-full min-h-0 flex-col bg-background"
    >
      {/* Matches the real ContentBoard's dashboard-archetype container so the
          skeleton doesn't reflow when the real content swaps in — from the
          same constant, so it cannot match it only until someone edits one. */}
      <div className={cn('mx-auto flex w-full min-h-0 flex-1 lg:gap-6', PAGE_MEASURE)}>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden bg-background p-4 pb-24 pt-4 sm:p-6 sm:pt-6 lg:pb-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="rounded-xl border border-border/60 bg-card/40 p-4">
                  <div className="mb-1.5 flex items-center justify-between">
                    <Skeleton className="size-4 rounded-sm bg-muted/40" />
                    <div className="flex gap-1">
                      <Skeleton className="size-6 rounded-full bg-muted/30" />
                      <Skeleton className="size-6 rounded-full bg-muted/30" />
                      <Skeleton className="size-6 rounded-full bg-muted/30" />
                    </div>
                  </div>
                  <Skeleton className="h-4 w-11/12 rounded-sm bg-muted/60" />
                  <Skeleton className="mt-2 h-3 w-4/5 rounded-sm bg-muted/40" />
                  <Skeleton className="mt-3 h-8 w-full rounded-full bg-muted/30" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {!hasGlobalRightDock ? (
          <aside className="hidden w-80 shrink-0 overflow-hidden bg-background pr-6 pt-6 lg:block">
            <div className="space-y-5">
              <div className="px-1">
                <Skeleton className="h-7 w-40 rounded-md bg-muted/50" />
              </div>

              <section className="space-y-2 px-1">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-24 rounded-sm bg-muted/60" />
                  <Skeleton className="h-3 w-16 rounded-sm bg-muted/40" />
                </div>
                <div className="flex items-center gap-1.5">
                  <Skeleton className="h-3 w-12 rounded-sm bg-muted/40" />
                  <Skeleton className="h-3 w-1 rounded-sm bg-muted/30" />
                  <Skeleton className="h-3 w-12 rounded-sm bg-muted/40" />
                  <Skeleton className="h-3 w-1 rounded-sm bg-muted/30" />
                  <Skeleton className="h-3 w-12 rounded-sm bg-muted/40" />
                </div>
              </section>

              <section className="space-y-2">
                <p className={cn(SECTION_TITLE, 'px-1')}>Pinned</p>
                <div className={cn(PANEL_CONTAINER, 'divide-y-0 p-1')}>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div key={index} className="px-2 py-2">
                      <Skeleton className="h-4 w-11/12 rounded-sm bg-muted/60" />
                      <Skeleton className="mt-1.5 h-3 w-3/4 rounded-sm bg-muted/40" />
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

export default ContentBoardSkeleton;
